import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { backupsAPI, domainsAPI } from '@hpanel/web-common/api/client'
import type { BackupResponse, Domain } from '@hpanel/web-common/api/types'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'
import { BackupList } from './BackupList'
import { UploadBackupForm } from './UploadBackupForm'

/**
 * Tenant Backups page — route: /client/backups
 *
 * Owns the data layer (backup list + domain list) and the single
 * data-testid="backups-error" error pane. All sub-components surface
 * errors via the onError callback so QA has one stable selector to
 * assert against (per Non-Negotiable §9: assert TEXT content, not just
 * visibility).
 */
export const BackupsPage: React.FC = () => {
  const { user } = useAuth()
  const [backups, setBackups] = useState<BackupResponse[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const refresh = async () => {
    try {
      const [bs, ds] = await Promise.all([
        backupsAPI.list(),
        domainsAPI.list(user?.id),
      ])
      if (!mountedRef.current) return
      setBackups(bs)
      setDomains(ds.domains)
      setError(null)
    } catch (e: unknown) {
      if (!mountedRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load backups')
    }
  }

  useEffect(() => {
    void (async () => {
      setLoading(true)
      await refresh()
      setLoading(false)
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Plan 20 P1: poll while any backup is still in flight. When the
  // user creates an account backup from /client/backups/new the
  // backend returns status="pending" immediately and a background
  // task does the work. We tick every 2 s until every visible row
  // is terminal, then stop — no permanent polling cost.
  useEffect(() => {
    const inFlight = backups.some(
      (b) => b.status === 'pending' || b.status === 'running',
    )
    if (!inFlight) return
    const id = window.setInterval(() => {
      if (!mountedRef.current) return
      void refresh()
    }, 2000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backups.map((b) => `${b.id}:${b.status}`).join('|')])

  return (
    <div data-testid="backups-page" className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Backups
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
            Create, download, and restore full backups of your site.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowUpload((v) => !v)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {showUpload ? 'Cancel' : 'Restore from file'}
          </button>
          {/* §15: multi-step backup creation lives on its own page,
              not in an inline dropdown form. The page can show
              resource-selection feedback, a kind picker, and a
              review step without fighting a 72-wide popover. */}
          <Link
            to="/client/backups/new"
            data-testid="backups-create-button"
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium inline-flex items-center"
          >
            + Create backup
          </Link>
        </div>
      </div>

      {/* Single shared error pane — all sub-components surface here */}
      {error && (
        <div
          data-testid="backups-error"
          className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 rounded-lg p-3 text-sm"
        >
          {error}
        </div>
      )}

      {/* Upload / restore form */}
      {showUpload && (
        <UploadBackupForm
          onRestored={() => {
            setShowUpload(false)
            void refresh()
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {/* Backup table */}
      {loading ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading backups…</p>
      ) : (
        <BackupList
          backups={backups}
          domains={domains}
          onDeleted={() => void refresh()}
          onError={(msg) => setError(msg)}
        />
      )}
    </div>
  )
}
